import React, { useState, useEffect } from 'react'
import { useFormContext, Controller, useFieldArray } from 'react-hook-form'
import { Form, Button } from 'react-bootstrap'
import { FaTrash, FaPlus, FaSearch, FaTimes } from 'react-icons/fa'
import { ProductVariantSearchModal } from '../ProductVariantSearchModal'
import { Variant, Product } from 'models/product'
import { cn } from 'components/UI/cn'
import apiService from 'services/api'

export function ProductSelectionStep() {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'line_items',
  })

  const [showProductSearch, setShowProductSearch] = useState(false)
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null)
  const [selectedVariants, setSelectedVariants] = useState<
    Map<
      number,
      {
        variant: Variant
        product: Product
      }
    >
  >(new Map())

  const api = apiService()
  const { data: products } = api.useGetProducts()

  // Load product/variant data for existing line items when editing
  useEffect(() => {
    if (!products || fields.length === 0) return

    const newVariantsMap = new Map(selectedVariants)
    let hasChanges = false

    fields.forEach((field: any, index) => {
      const variantId = field.variant_id
      if (variantId && variantId > 0 && !selectedVariants.has(index)) {
        // Find the product and variant for this variant_id
        for (const product of products) {
          const variant = product.variants?.find((v) => v.id === variantId)
          if (variant) {
            newVariantsMap.set(index, { variant, product })
            hasChanges = true
            break
          }
        }
      }
    })

    if (hasChanges) {
      setSelectedVariants(newVariantsMap)
    }
  }, [fields, products])

  const handleSelectVariant = (variant: Variant, product: Product) => {
    if (currentEditIndex !== null) {
      // Update the variant ID in the form
      setValue(`line_items.${currentEditIndex}.variant_id`, variant.id || 0, {
        shouldValidate: true,
      })

      // Default product_format to product's product_format
      setValue(
        `line_items.${currentEditIndex}.product_format`,
        product.product_format || '',
        {
          shouldValidate: true,
        }
      )

      // Store the selected variant details
      setSelectedVariants((prev) => {
        const newMap = new Map(prev)
        newMap.set(currentEditIndex, { variant, product })
        return newMap
      })
    }
  }

  const handleClearVariant = (index: number) => {
    setValue(`line_items.${index}.variant_id`, 0, { shouldValidate: true })
    setSelectedVariants((prev) => {
      const newMap = new Map(prev)
      newMap.delete(index)
      return newMap
    })
  }

  const handleOpenSearch = (index: number) => {
    setCurrentEditIndex(index)
    setShowProductSearch(true)
  }

  return (
    <div>
      <h5 className="text-lg font-medium mb-4">Products</h5>

      {fields.map((field, index) => {
        const selectedVariantData = selectedVariants.get(index)
        return (
          <div
            key={field.id}
            className="flex gap-4 items-center mb-4 p-4 border border-gray-300 rounded-lg bg-gray-50"
          >
            <div className="flex-1 flex gap-4 items-center">
              <div className="flex-[2]">
                <Form.Label className="font-semibold mb-2">
                  Product Variant *
                </Form.Label>
                {selectedVariantData ? (
                  <>
                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-md mb-2">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">
                          {selectedVariantData.product.name} -{' '}
                          {selectedVariantData.variant.name}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {selectedVariantData.variant.format_pack_size && (
                            <span>
                              {selectedVariantData.variant.format_pack_size}
                            </span>
                          )}
                          {selectedVariantData.variant.sku && (
                            <span className="ml-4">
                              SKU: {selectedVariantData.variant.sku}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleClearVariant(index)}
                        className="py-1 px-2 text-sm"
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenSearch(index)}
                    className={cn(
                      'flex items-center gap-2 border-fb-dark-green text-fb-dark-green',
                      'hover:bg-fb-dark-green hover:text-white hover:border-fb-dark-green',
                      'focus:shadow-[0_0_0_0.2rem_rgba(44,107,78,0.25)]'
                    )}
                  >
                    <FaSearch />
                    Search for Product Variant
                  </Button>
                )}
                {(errors.line_items as any)?.[index]?.variant_id && (
                  <div className="text-red-600 text-sm mt-1">
                    {
                      (errors.line_items as any)[index].variant_id
                        .message as string
                    }
                  </div>
                )}
                <Controller
                  name={`line_items.${index}.variant_id`}
                  control={control}
                  render={({ field }) => <input type="hidden" {...field} />}
                />
              </div>

              <div className="flex-[0.8]">
                <Form.Label className="font-semibold mb-2">
                  Quantity *
                </Form.Label>
                <Controller
                  name={`line_items.${index}.quantity`}
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Control
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        min={1}
                        isInvalid={
                          !!(errors.line_items as any)?.[index]?.quantity
                        }
                        placeholder="Qty"
                      />
                      {(errors.line_items as any)?.[index]?.quantity && (
                        <div className="text-red-600 text-sm mt-1">
                          {
                            (errors.line_items as any)[index].quantity
                              .message as string
                          }
                        </div>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="flex-[0.8]">
                <Form.Label className="font-semibold mb-2">
                  Product Format
                </Form.Label>
                <Controller
                  name={`line_items.${index}.product_format`}
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder="e.g., case"
                        isInvalid={
                          !!(errors.line_items as any)?.[index]?.product_format
                        }
                      />
                      {(errors.line_items as any)?.[index]?.product_format && (
                        <div className="text-red-600 text-sm mt-1">
                          {
                            (errors.line_items as any)[index].product_format
                              .message as string
                          }
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={() => {
                remove(index)
                // Also remove from selectedVariants map
                setSelectedVariants((prev) => {
                  const newMap = new Map(prev)
                  newMap.delete(index)
                  return newMap
                })
              }}
              className="align-self-center"
            >
              <FaTrash />
            </Button>
          </div>
        )
      })}

      {errors.line_items && typeof errors.line_items.message === 'string' && (
        <div className="text-red-600 text-sm mt-1">
          {errors.line_items.message}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={() =>
          append({ variant_id: 0, quantity: 1, product_format: '' })
        }
        className={cn(
          'mt-4 w-full flex items-center justify-center border-fb-dark-green text-fb-dark-green',
          'hover:bg-fb-dark-green hover:text-white hover:border-fb-dark-green',
          'focus:shadow-[0_0_0_0.2rem_rgba(44,107,78,0.25)]'
        )}
      >
        <FaPlus className="me-2" />
        Add Product
      </Button>

      {/* Product Variant Search Modal */}
      <ProductVariantSearchModal
        open={showProductSearch}
        onClose={() => setShowProductSearch(false)}
        onSelectVariant={handleSelectVariant}
      />
    </div>
  )
}
